import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Box,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
} from "@mui/material";
import { API_URI } from "../../constants/GlobalConstants";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { getImgOrderCreditData } from "../../redux/actions/ImageToOrderActions";
import HomeIcon from "@mui/icons-material/Home";
import CameraEnhanceIcon from "@mui/icons-material/CameraEnhance";

const ImageToOrder = ({ totalCredits }) => {
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [s3Url, setS3Url] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [parsedData, setParsedData] = useState([]);
  const [extractionInProgress, setExtractionInProgress] = useState(false);
  const [s3key, setS3Key] = useState("");
  const creditRecord = useSelector((state) => state.ImgOrderCredit);
  const {
    loading: imgOrderLoading,
    error: imgOrderError,
    creditRecord: data,
  } = creditRecord;
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setImagePreview(URL.createObjectURL(file));
    setS3Url("");
    setParsedData([]);
    setError("");
  };
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // useEffect(() => {
  //   dispatch(getImgOrderCreditData());
  // }, [dispatch]);

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      setLoading(true);
      setExtractionInProgress(true);

      const response = await axios.post(`${API_URI}/api/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${userInfo.token}`,
        },
      });

      setTimeout(() => {
        setExtractionInProgress(false);
        setS3Url(response.data.url);
        setParsedData(response.data.orderData);
        setS3Key(response.data.key);
      }, 3000);
    } catch (err) {
      setError("Failed to upload file.");
      console.error(err);
    } finally {
      setLoading(false);
      dispatch(getImgOrderCreditData());

    }
  };

  const handleInputChange = (index, field, value) => {
    const updatedData = [...parsedData];
    updatedData[index][field] = value;
    setParsedData(updatedData);
  };

  const handleAddRow = () => {
    setParsedData([...parsedData, { width: "", height: "" }]);
  };

  const handleDeleteRow = (index) => {
    const updatedData = parsedData.filter((_, i) => i !== index);
    setParsedData(updatedData);
  };

  const handleConfirmOrder = async () => {
    if (!s3Url || parsedData.length === 0) {
      setError(
        "Please upload an image and extract data before confirming the order."
      );
      return;
    }

    const orderData = {
      status: "draft",
      imageUrl: s3key,
      extractedData: parsedData,
    };

    try {
      setLoading(true);

      const response = await axios.post(
        `${API_URI}/api/img-to-text/create-img-to-text`,
        orderData,
        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      );

      // Handle the response (e.g., redirect to order list or display success message)

      setError(""); // Clear any previous errors
      setParsedData([]); // Reset the parsed data
      setS3Url(""); // Reset the S3 URL
      setImagePreview(null); // Clear the image preview
      navigate(`/order/img-to-order/${response.data._id}`);
    } catch (err) {
      setError("Failed to confirm the order.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      handleFileChange({ target: { files: [file] } });
    }
  };

  if (totalCredits <= 0) {
    return (
      <Box
        sx={{
          p: 6,
          textAlign: "center",
          background: "linear-gradient(135deg, #fff8f8 0%, #fff 100%)",
        }}
      >
        <CameraEnhanceIcon
          sx={{
            fontSize: 80,
            color: "error.main",
            mb: 2,
            opacity: 0.8,
          }}
        />
        <Typography
          variant="h4"
          color="error.main"
          gutterBottom
          fontWeight="700"
          sx={{ mb: 2 }}
        >
          Image Order Not Available
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 600, mx: "auto" }}
        >
          You currently don't have any credits available to create an image
          order. Please contact the administrator to add credits to your
          account.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          href="mailto:admin@example.com"
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "1.1rem",
          }}
        >
          Contact Admin
        </Button>
      </Box>
    );
  }

  return (
    <div className="container mt-4">
      <Typography variant="h4" className="text-center mb-4">
        Image to Order
      </Typography>
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={4}>
          <Box
            component={Card}
            className="shadow"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                className="d-flex align-items-center"
              >
                <ImageIcon className="me-2" />
                Upload Image
              </Typography>

              <Box className="p-4 mb-3 bg-white rounded text-center border">
                {!imagePreview ? (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="d-none"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="mb-0 w-100 cursor-pointer"
                    >
                      <Box className="d-flex flex-column align-items-center">
                        <CloudUploadIcon
                          sx={{ fontSize: 48, color: "primary.main" }}
                          className="mb-2"
                        />
                        <Typography variant="body1" className="mb-1 text-dark">
                          Drag & drop an image here or click to browse
                        </Typography>
                      </Box>
                    </label>

                    {/* Camera Capture Button */}
                    <input
                      type="file"
                      accept="image/*"
                      capture="camera" // This will prompt the user to take a photo using their device's camera
                      onChange={handleFileChange}
                      className="d-none"
                      id="image-capture"
                    />
                    <label
                      htmlFor="image-capture"
                      className="mb-0 w-100 cursor-pointer"
                    >
                      <Box className="d-flex flex-column align-items-center mt-3">
                        <CameraAltIcon
                          sx={{ fontSize: 48, color: "primary.main" }}
                          className="mb-2"
                        />
                        <Typography variant="body1" className="mb-1 text-dark">
                          Take a photo with your camera
                        </Typography>
                      </Box>
                    </label>
                  </>
                ) : (
                  <Box className="text-center">
                    <img
                      src={imagePreview}
                      alt="Uploaded Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "300px",
                        objectFit: "contain",
                        borderRadius: "8px",
                        cursor: "pointer",
                        display: "block",
                        margin: "0 auto",
                      }}
                      onClick={() =>
                        document.getElementById("image-upload").click()
                      }
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="d-none"
                      id="image-upload"
                    />
                    <Typography variant="body2" className="mt-2 text-muted">
                      Click the image to upload a new one
                    </Typography>
                  </Box>
                )}
              </Box>

              <Button
                onClick={handleUpload}
                disabled={loading}
                variant="contained"
                color="primary"
                fullWidth
                startIcon={!loading && <CloudUploadIcon />}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Upload & Extract"
                )}
              </Button>
              {extractionInProgress && (
                <Box mt={2}>
                  <LinearProgress />
                  <Typography align="center" variant="body2" className="mt-2">
                    Extracting text... Please wait.
                  </Typography>
                </Box>
              )}

              {error && (
                <Typography color="error" className="mt-3">
                  {error}
                </Typography>
              )}
            </CardContent>
          </Box>
        </Grid>

        {parsedData.length > 0 && (
          <Grid item xs={12}>
            <Card className="shadow">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Extracted Data
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="mt-3 text-center"
                >
                  Note: The extracted results are generated using advanced
                  technology to provide accurate outputs. However, some results
                  may require manual review and adjustments for the best
                  accuracy.
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Width</TableCell>
                        <TableCell>Height</TableCell>
                        <TableCell>Remarks</TableCell>{" "}
                        {/* Add remarks column */}
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedData.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <TextField
                              value={row.width}
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "width",
                                  e.target.value
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={row.height}
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "height",
                                  e.target.value
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={row.remarks}
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "remarks",
                                  e.target.value
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleDeleteRow(index)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Button
                            onClick={handleAddRow}
                            startIcon={<AddIcon />}
                            variant="contained"
                          >
                            Add Row
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirmOrder}
                    fullWidth
                  >
                    Confirm Order
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default ImageToOrder;
